import {
  NONE_STUDENT_DEFAULT_TAX_CODE,
  STUDENT_DEFAULT_TAX_CODE
} from '@vivaldis/common'
import { EmployeeInput } from '@vivaldis/graphql'
import { MaritalStatesEnum } from '@vivaldis/session'
import {
  Formik,
  FormikContextType,
  FormikHelpers,
  useFormikContext
} from 'formik'
import { PropsWithChildren, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import { Employee } from '../types/Employee'
import { employeeFormValuesToEmployeeInput } from '../utils/employeeFormValuesToEmployeeInput'

export interface EditEmployeeProfileFormValues {
  maritalState: MaritalStatesEnum | null
  maritalDate: Date | null
  childrenNumber: number | null
  childrenWithDisabilitiesNumber: number | null
  dependentPersonsNumber: number | null
  dependentPersonsWithDisabilitiesNumber: number | null
  isStudent: boolean
  taxCode: string | null
  involuntaryPartTime: boolean | null
}

export function useForm(): FormikContextType<EditEmployeeProfileFormValues> {
  return useFormikContext()
}

export interface EmployeeFormProviderProps {
  employee: Employee
  onSubmit: (
    employeeInput: EmployeeInput,
    formikHelpers: FormikHelpers<EditEmployeeProfileFormValues>
  ) => Promise<void>
}

export const FormProvider = ({
  employee,
  onSubmit,
  children
}: PropsWithChildren<EmployeeFormProviderProps>) => {
  const [t] = useTranslation('web_ui')

  const defaultTaxCode = useMemo(() => {
    const isStudent = employee.isStudent || false
    // The tax should be default (and mandatory) 11,11% for students
    // The tax should be default 23% for non-students
    return employee.workProfile?.taxCode
      ? employee.workProfile?.taxCode
      : isStudent
        ? STUDENT_DEFAULT_TAX_CODE
        : NONE_STUDENT_DEFAULT_TAX_CODE
  }, [employee.isStudent, employee.workProfile?.taxCode])

  const initialFormValues: EditEmployeeProfileFormValues = useMemo(
    () => ({
      // Work profile
      maritalState: employee.workProfile?.maritalState || null,
      maritalDate: employee.workProfile?.maritalDate
        ? new Date(employee.workProfile?.maritalDate)
        : null,
      childrenNumber: employee.workProfile?.childrenNumber || null,
      childrenWithDisabilitiesNumber:
        employee.workProfile?.childrenWithDisabilitiesNumber || null,
      dependentPersonsNumber:
        employee.workProfile?.dependentPersonsNumber || null,
      dependentPersonsWithDisabilitiesNumber:
        employee.workProfile?.dependentPersonsWithDisabilitiesNumber || null,
      isStudent: employee.isStudent,
      taxCode: defaultTaxCode,
      // involuntaryPartTime should be "false" by default
      involuntaryPartTime: employee.workProfile?.involuntaryPartTime ?? false
    }),
    [
      defaultTaxCode,
      employee.isStudent,
      employee.workProfile?.childrenNumber,
      employee.workProfile?.childrenWithDisabilitiesNumber,
      employee.workProfile?.dependentPersonsNumber,
      employee.workProfile?.dependentPersonsWithDisabilitiesNumber,
      employee.workProfile?.involuntaryPartTime,
      employee.workProfile?.maritalDate,
      employee.workProfile?.maritalState
    ]
  )

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        maritalState: Yup.string()
          .label(t('edit_employee_wage_profile_form.marital_state.label'))
          .nullable(),
        maritalDate: Yup.date()
          .label(t('edit_employee_wage_profile_form.marital_date.label'))
          .nullable(),
        childrenNumber: Yup.number()
          .label(t('edit_employee_wage_profile_form.children_number.label'))
          .nullable(),
        childrenWithDisabilitiesNumber: Yup.number()
          .label(
            t(
              'edit_employee_wage_profile_form.children_with_disabilities_number.label'
            )
          )
          .nullable(),
        dependentPersonsNumber: Yup.number()
          .label(
            t('edit_employee_wage_profile_form.dependent_persons_number.label')
          )
          .nullable(),
        dependentPersonsWithDisabilitiesNumber: Yup.number()
          .label(
            t(
              'edit_employee_wage_profile_form.dependent_persons_with_disabilities_number.label'
            )
          )
          .nullable(),
        isStudent: Yup.boolean()
          .label(t('edit_employee_wage_profile_form.is_student.label'))
          .required(),
        taxCode: Yup.string()
          .label(t('edit_employee_wage_profile_form.tax_code.label'))
          .nullable()
          .required(),
        involuntaryPartTime: Yup.boolean()
          .label(
            t('edit_employee_wage_profile_form.involuntary_part_time.label')
          )
          .nullable()
      }),
    [t]
  )

  const handleSubmit = useCallback(
    async (
      values: EditEmployeeProfileFormValues,
      formikHelpers: FormikHelpers<EditEmployeeProfileFormValues>
    ) => {
      const employeeInput = employeeFormValuesToEmployeeInput(values)
      await onSubmit(employeeInput, formikHelpers)
      formikHelpers.setSubmitting(false)
    },
    [onSubmit]
  )

  return (
    <Formik<EditEmployeeProfileFormValues>
      initialValues={initialFormValues}
      validateOnChange={false}
      validateOnBlur={true}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {children}
    </Formik>
  )
}
